import React from 'react'
import { Formik, FormikProps } from 'formik'
import * as yup from 'yup'

import Form from 'components/Form'
import Button from 'components/Button'
import PasswordInput from 'components/PasswordInput'
import { useSignIn } from 'modules/user/operations'
import { ParsedError } from 'graphql/apollo/useMutation'

const INITIAL_VALUES = {
  password: '',
}

type Values = typeof INITIAL_VALUES

interface ErrorsType {
  errors?: ParsedError
}

const signInSchema = yup.object().shape({
  password: yup.string().required('Password can’t be blank'),
})

const PasswordField = ({
  touched,
  errors,
  values,
  handleChange,
  handleBlur,
}: FormikProps<Values>) => (
  <Form.Field
    name="password"
    label="Password"
    errorText={touched.password ? errors.password : undefined}>
    <PasswordInput
      name="password"
      placeholder="••••••••"
      value={values.password}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  </Form.Field>
)

export const Errors: React.FC<ErrorsType> = ({ errors }) =>
  errors?.message ? <Form.ErrorField>{errors.message}</Form.ErrorField> : null

const Submit = ({ isLoading }: { isLoading: boolean }) => (
  <Form.Footer>
    <Button
      variant="primary"
      type="submit"
      disabled={isLoading}
      loading={isLoading}
      fill>
      Sign In
    </Button>
  </Form.Footer>
)

interface SignInWithPasswordFormProps {
  email: string
  onCompleted: () => void
}

const SignInWithPasswordForm = ({
  email,
  onCompleted,
}: SignInWithPasswordFormProps) => {
  const [signIn, { loading, error }] = useSignIn({
    onCompleted,
  })

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      onSubmit={async ({ password }) => {
        try {
          await signIn({ variables: { email, password } })
        } catch (e) {
          // The error handling should be taken care of by Apollo
        }
      }}
      validationSchema={signInSchema}
      validateOnBlur={false}>
      {formikProps => (
        <Form>
          <PasswordField {...formikProps} />
          <Errors errors={error} />
          <Submit isLoading={loading} />
        </Form>
      )}
    </Formik>
  )
}

export default SignInWithPasswordForm
